import { Node, Element, Transforms, Editor, Path } from 'slate';
import type { NodeEntry } from 'slate';

import { options } from 'common/components/ContentEditor/options';
import {
    asEditor,
    type TTypedElement,
} from 'common/components/ContentEditor/utils/editorHelpers';

// Both list items and blockquote paragraphs only ever contain a single
// nested paragraph elsewhere in this editor (see editorHelpers.ts's
// unwrapList/unwrapBlockquote and their test fixtures) - anything else
// (a table, an embed, a nested list) is disallowed content, most often
// from legacy imported content or an insert that landed at the wrong
// nesting level.
const PARAGRAPH_ONLY_CONTAINER_TYPES = [
    options.li.type,
    options.blockquote.type,
];

export const withNormalize = ({
    editor,
    tf: { normalizeNode },
}: {
    editor: Record<string, unknown>;
    tf: { normalizeNode: (entry: NodeEntry) => void };
}) => {
    const slateEditor = asEditor(editor);

    return {
        transforms: {
            normalizeNode(entry: NodeEntry) {
                const [node, path] = entry;

                if (
                    Element.isElement(node) &&
                    PARAGRAPH_ONLY_CONTAINER_TYPES.includes(
                        (node as TTypedElement).type
                    )
                ) {
                    for (const [child, childPath] of Node.children(
                        slateEditor,
                        path
                    )) {
                        if (
                            Element.isElement(child) &&
                            !Editor.isInline(slateEditor, child) &&
                            (child as TTypedElement).type !== options.p.type
                        ) {
                            // Lift disallowed block content (e.g. a nested
                            // list/table/embed) out to sit beside the
                            // container instead of deleting it.
                            Transforms.moveNodes(slateEditor, {
                                at: childPath,
                                to: Path.next(path),
                            });

                            return;
                        }
                    }
                }

                normalizeNode(entry);
            },
        },
    };
};
